// @flow
import type { ActivePassengerBooking } from 'types/dashboard';
import type { BookingRequest } from 'types/bookings';
import type { Errors } from 'types/errors';
import type { Location } from 'types/entities';

import React, { Component } from 'react';
import autobind from 'react-autobind';
import _ from 'lodash';
import classnames from 'classnames';
import { Map, List } from 'immutable';

import { roundCoordinate } from 'utils/map-utils';
import { entityURLForCohort } from 'utils/cohort-utils';
import { PAYMENT_TYPES, REWARD_TYPES } from 'utils/library-utils';

import withEscapeToDismiss from 'components/others/WithEscapeToDismiss';
import InputLocation from 'components/form/InputLocation';
import ErrorNotification from 'components/others/ErrorNotification';

import './CreateBookingPanel.scss';

type Props = {
  activePassengerBooking?: ActivePassengerBooking,
  onUpdate: Function,
  onCreate: Function,
  onShow: Function,
  onHide: Function,
  handleClose: Function,
  isLoading: boolean,
  errors: Errors,
  successBookingID: ?string,
  isGetTaxiTypesPending: boolean,
  taxiTypesErrors: Errors,
  nearbyTaxiTypes: Array<any>,
  pointOfInterests: Map,
  getPOI: Function,
  clearError: Function,
};

type State = {
  pickUpValid: boolean,
  dropOffValid: boolean,
  remarks: string,
  extraDropOffValids: Array<boolean>,
};

@withEscapeToDismiss()
export default class CreateBookingPanel extends Component {
  static defaultProps = {
    activePassengerBooking: null,
  }

  constructor(props: Props) {
    super(props);
    this.state = { pickUpValid: true, dropOffValid: true, extraDropOffValids: [], remarks: '' };
    autobind(this);
  }

  state: State;

  componentDidMount() {
    this.props.onShow();
    this.taxiTypeID.focus();
  }

  componentWillReceiveProps(nextState: Props) {
    // Set the pick up/drop off location as valid if the location was changed.
    if (this.props.activePassengerBooking != null && nextState.activePassengerBooking != null) {
      const currentBooking = this.props.activePassengerBooking;
      const nextBooking = nextState.activePassengerBooking;

      if (!_.isEqual(currentBooking.pickUpLocation, nextBooking.pickUpLocation)) {
        this.setState({ pickUpValid: true });
      }

      if (!_.isEqual(currentBooking.dropOffLocation, nextBooking.dropOffLocation)) {
        this.setState({ dropOffValid: true });
      }

      // when the first element of extraDropOffLocations is empty,
      // it is a brand new booking
      if (_.isEmpty(nextBooking.extraDropOffLocations[0])) {
        this.setState({ extraDropOffValids: [] });
        this.pushDropOffBtn.removeAttribute('disabled');
        this.popDropOffBtn.setAttribute('disabled', true);
      }
    }
  }

  componentWillUnmount() {
    this.props.onHide();
  }

  pickUp: InputLocation;
  dropOff: InputLocation;
  taxiTypeID: Object;
  rewardType: string;
  rewardValue: string;
  props: Props;

  pushDropOff(paxBkg: any) {
    return () => {
      if (this.state.extraDropOffValids.length === 1) {
        return;
      }

      this.setState(curState => ({
        extraDropOffValids: _.concat(curState.extraDropOffValids, true),
      }));

      const location = [
        roundCoordinate(paxBkg.pickUpLocation[0] + 0.01),
        roundCoordinate(paxBkg.pickUpLocation[1] + 0.01),
      ];

      const { onUpdate, getPOI } = this.props;

      onUpdate({
        ...paxBkg,
        extraDropOffLocations: [location],
      });
      getPOI(location);

      this.pushDropOffBtn.setAttribute('disabled', true);
      this.popDropOffBtn.removeAttribute('disabled');
    };
  }

  popDropOff(paxBkg: any) {
    return () => {
      if (this.state.extraDropOffValids.length === 0) {
        return;
      }

      this.setState(curState => ({
        extraDropOffValids: _.dropRight(curState.extraDropOffValids),
      }));

      this.props.onUpdate({
        ...paxBkg,
        extraDropOffLocations: [[]],
      });

      this.pushDropOffBtn.removeAttribute('disabled');
      this.popDropOffBtn.setAttribute('disabled', true);
    };
  }

  createTaxiTypeOptions() {
    return _.map(this.props.nearbyTaxiTypes || [], category => (
      <optgroup label={category.group} key={category.group}>
        {_.map(category.nearbyTaxiTypes, taxiType => (
          <option value={taxiType.id} key={taxiType.id}>{taxiType.name}</option>
        ))}
      </optgroup>
      )
    );
  }

  renderPOI(location: Location, extraDrop: boolean) {
    const { pointOfInterests } = this.props;
    const key = List(location);
    if (pointOfInterests && pointOfInterests.has(key)) {
      return (
        <p className={classnames('help', 'is-success', {
          'extra-drop-off': extraDrop,
        })}
        >
          {pointOfInterests.get(key)}
        </p>
      );
    }

    return null;
  }

  render() {
    const activePassengerBooking = this.props.activePassengerBooking || {};
    return (
      <div className="create-booking-panel">
        <button
          aria-label="Close"
          className="is-pulled-right delete" onClick={() => {
            this.props.handleClose();
          }}
        />
        <h3 className="title is-5">Create booking - Passenger ID: {activePassengerBooking.passengerID}
        </h3>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const bookingRequest: BookingRequest = {
              taxiTypeID: activePassengerBooking.taxiTypeID,
              paymentType: activePassengerBooking.paymentType,
              remarks: this.state.remarks,
              pickUpLongitude: activePassengerBooking.pickUpLocation[0],
              pickUpLatitude: activePassengerBooking.pickUpLocation[1],
              dropOffLongitude: activePassengerBooking.dropOffLocation[0],
              dropOffLatitude: activePassengerBooking.dropOffLocation[1],
              extraDropOffs: activePassengerBooking.extraDropOffLocations,
            };
            if (activePassengerBooking.rewardValue) {
              bookingRequest[activePassengerBooking.rewardType] = activePassengerBooking.rewardValue;
            }
            this.props.onCreate(bookingRequest);
          }}
        >
          <div className="columns is-multiline is-mobile">
            <div className="column is-one-third">
              <label className="label" htmlFor="pick-up-location">Pick Up</label>
              <InputLocation
                name="pick-up-location"
                className={classnames({
                  'is-danger': !this.state.pickUpValid,
                })}
                value={activePassengerBooking.pickUpLocation}
                ref={(value) => { this.pickUp = value; }}
                onBeforeChange={() => {
                  this.setState({ pickUpValid: this.pickUp.isValid() });
                }}
                onChange={(value) => {
                  this.props.onUpdate({
                    ...activePassengerBooking,
                    pickUpLocation: value,
                  });
                }}
              />
              {this.renderPOI(activePassengerBooking.pickUpLocation, false)}
            </div>

            <div className="column is-one-third">
              <label className="label" htmlFor="drop-off-location">
                <span>Drop Off</span>
                <div className="is-pulled-right">
                  <a
                    className="button is-primary is-small gap"
                    title="Add Extra Dropoff" aria-label="Add Extra Dropoff"
                    ref={(value) => { this.pushDropOffBtn = value; }}
                    onClick={this.pushDropOff(activePassengerBooking)}
                  >
                    <span className="icon is-small" aria-hidden="true"><i className="fa fa-plus" /></span>
                  </a>
                  <a
                    className="button is-primary is-small"
                    title="Remove Extra Dropoff" aria-label="Remove Extra Dropoff"
                    ref={(value) => { this.popDropOffBtn = value; }}
                    onClick={this.popDropOff(activePassengerBooking)}
                    disabled
                  >
                    <span className="icon is-small" aria-hidden="true"><i className="fa fa-minus" /></span>
                  </a>
                </div>
              </label>
              <InputLocation
                name="extra-drop-off-location-1"
                className={classnames({
                  'is-danger': !this.state.extraDropOffValids[0],
                  'is-hidden': this.state.extraDropOffValids.length === 0,
                })}
                value={activePassengerBooking.extraDropOffLocations[0]}
                ref={(value) => { this.xdropOff = value; }}
                onBeforeChange={() => {
                  this.setState((curState) => {
                    const extraDropOffValids = _.slice(curState.extraDropOffValids);
                    extraDropOffValids[0] = this.xdropOff.isValid();

                    return {
                      extraDropOffValids,
                    };
                  });
                }}
                onChange={(value) => {
                  const extraDropOffLocations = _.slice(activePassengerBooking.extraDropOffLocations);
                  extraDropOffLocations[0] = value;
                  this.props.onUpdate({
                    ...activePassengerBooking,
                    extraDropOffLocations,
                  });
                }}
              />
              {this.state.extraDropOffValids.length > 0 &&
                this.renderPOI(activePassengerBooking.extraDropOffLocations[0], true)}
              <InputLocation
                name="drop-off-location"
                className={classnames({
                  'is-danger': !this.state.dropOffValid,
                })}
                value={activePassengerBooking.dropOffLocation}
                ref={(value) => { this.dropOff = value; }}
                onBeforeChange={() => {
                  this.setState({ dropOffValid: this.dropOff.isValid() });
                }}
                onChange={(value) => {
                  this.props.onUpdate({
                    ...activePassengerBooking,
                    dropOffLocation: value,
                  });
                }}
              />
              {this.renderPOI(activePassengerBooking.dropOffLocation, false)}
            </div>
            <div className="column is-one-third">
              <label className="label" htmlFor="taxi-type-id">Taxi Type ID</label>
              <div className="control">
                <div
                  className={classnames('select', {
                    'is-loading': this.props.isGetTaxiTypesPending,
                  })}
                >
                  <select
                    className="is-focused"
                    name="taxi-type-id"
                    onChange={(event) => {
                      this.props.onUpdate({
                        ...activePassengerBooking,
                        taxiTypeID: event.target.value,
                      });
                    }}
                    value={activePassengerBooking.taxiTypeID}
                    ref={(input) => { this.taxiTypeID = input; }}
                  >
                    <option value="">Select a Taxi Type</option>
                    {this.createTaxiTypeOptions()}
                  </select>
                </div>
              </div>
            </div>

            <div className="column is-one-third">
              <label className="label" htmlFor="remarks">Remarks</label>
              <div className="field">
                <div className="control">
                  <textarea
                    name="remarks"
                    className="textarea"
                    rows="3"
                    value={this.state.remarks}
                    onChange={(event) => {
                      this.setState({
                        remarks: event.target.value,
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="column is-one-third">
              <label className="label" htmlFor="promo-or-reward">Promo/Reward</label>
              <div className="field has-addons">
                <p className="control">
                  <span className="select">
                    <select
                      name="promo-or-reward"
                      onChange={(event) => {
                        this.props.onUpdate({
                          ...activePassengerBooking,
                          rewardType: event.target.value,
                          rewardValue: '',
                        });
                      }}
                      value={activePassengerBooking.rewardType}
                      ref={(value) => { this.rewardType = value; }}
                    >
                      {REWARD_TYPES.map(reward =>
                        <option value={reward.name} key={reward.name}>{reward.display}</option>
                      )}
                    </select>
                  </span>
                </p>

                <p className="control">
                  <input
                    className="input"
                    onChange={(event) => {
                      this.props.onUpdate({
                        ...activePassengerBooking,
                        rewardValue: event.target.value,
                      });
                    }}
                    value={activePassengerBooking.rewardValue}
                    ref={(input) => { this.rewardValue = input; }}
                  />
                </p>
              </div>
            </div>

            <div className="column is-one-third">
              <div className="columns">
                <div className="column is-half">
                  <label className="label" htmlFor="paymentType">Pay By</label>
                  <span className="select">
                    <select
                      name="paymentType"
                      onChange={(event) => {
                        this.props.onUpdate({
                          ...activePassengerBooking,
                          paymentType: event.target.value,
                        });
                      }}
                      value={activePassengerBooking.paymentType}
                      ref={(value) => { this.paymentType = value; }}
                    >
                      {PAYMENT_TYPES.map(payment =>
                        <option value={payment.name} key={payment.name}>{payment.display}</option>
                      )}
                    </select>
                  </span>
                </div>
                <div className="column is-half">
                  <label className="label" htmlFor>&nbsp;</label>
                  <div className="control is-grouped">
                    <p className="control">
                      <button
                        type="submit" className={classnames('button is-info', {
                          'is-loading': this.props.isLoading,
                          'is-disabled': this.props.isLoading,
                        })}
                      >
                        Book
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
        <br />
        {!_.isEmpty(this.props.errors) &&
          <ErrorNotification
            errors={this.props.errors}
            dismissible
            onClose={() => {
              this.props.clearError();
            }}
          />
        }
        {this.props.successBookingID &&
          <div className="notification is-success" role="alert">
            <p>Booking{' '}
              <a
                href={`${entityURLForCohort()}/bookings/${String(this.props.successBookingID)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {this.props.successBookingID}
              </a> has been created.
            </p>
          </div>
        }
      </div>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/js/views/dashboard/bookings/CreateBookingPanel.jsx
 **/